import { Link } from "react-router-dom";
import { Reveal } from "@/components/Motion";
import { ShieldCheck, MessageSquare, ArrowLeft } from "lucide-react";
import { BRAND } from "@/lib/brand";

const UPDATED = "June 2026";

export default function Privacy() {
  return (
    <div className="max-w-[900px] mx-auto px-5 md:px-10 pt-36 pb-28 min-h-screen" data-testid="privacy-page">
      <Reveal>
        <span className="mono-label text-cyan">Legal</span>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mt-4 mb-3">Privacy Policy</h1>
        <p className="text-white/40 font-mono text-sm mb-10">Last updated: {UPDATED}</p>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="glass rounded-2xl p-8 border border-white/10 mb-6 text-white/60 leading-relaxed space-y-4">
          <ShieldCheck className="text-cyan mb-1" size={26} />
          <p>
            {BRAND.name} ("we", "us", "our") respects your privacy. This policy explains what information we collect,
            how we use it, and the choices you have. By using our website and services you agree to this policy.
          </p>

          <h2 className="font-display text-2xl text-white pt-2">Information we collect</h2>
          <ul className="space-y-2 list-disc pl-5">
            <li><b className="text-white">Contact details</b> — name, email address, phone/mobile number, and shipping/service address you provide when you create an account, place an order, bid in an auction, book a service, or apply for wholesale.</li>
            <li><b className="text-white">Order & booking details</b> — items purchased, auction bids, service selections and scheduling notes.</li>
            <li><b className="text-white">Payment information</b> — processed securely by Stripe. We do not store your full card details on our servers.</li>
          </ul>

          <h2 className="font-display text-2xl text-white pt-2">How we use your information</h2>
          <ul className="space-y-2 list-disc pl-5">
            <li>To process orders, deposits, auctions and service bookings.</li>
            <li>To contact you about your account, bids, purchases and appointments.</li>
            <li>To send transactional notifications by email and, if you provide a mobile number, by SMS text message (see below).</li>
            <li>To provide customer support and improve our services.</li>
          </ul>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="glass rounded-2xl p-8 border border-cyan/25 mb-6 text-white/60 leading-relaxed space-y-4" id="sms" data-testid="privacy-sms">
          <MessageSquare className="text-cyan mb-1" size={26} />
          <h2 className="font-display text-2xl text-white">SMS text message notifications</h2>
          <p>
            If you provide your mobile number, {BRAND.name} may send you transactional SMS text messages related to your
            activity — for example when you are outbid or win an auction, and when your service booking is confirmed.
          </p>
          <ul className="space-y-2 list-disc pl-5">
            <li><b className="text-white">Opt-in:</b> You consent to receive SMS by voluntarily providing your mobile number during registration, checkout or booking. Consent is not a condition of purchase.</li>
            <li><b className="text-white">Opt-out:</b> You can opt out at any time by replying <b className="text-white">STOP</b> to any message. Reply <b className="text-white">HELP</b> for help.</li>
            <li><b className="text-white">Message frequency</b> varies based on your activity. Message and data rates may apply.</li>
            <li><b className="text-white">Your SMS opt-in and mobile number are never sold or shared with third parties</b> for their marketing purposes. We share it only with our messaging provider (Twilio) solely to deliver these messages.</li>
          </ul>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="glass rounded-2xl p-8 border border-white/10 mb-6 text-white/60 leading-relaxed space-y-4">
          <h2 className="font-display text-2xl text-white">Sharing & security</h2>
          <p>
            We share data only with service providers who help us operate — such as Stripe (payments) and Twilio (SMS) —
            and only to the extent needed to provide the service. We do not sell your personal information. We use
            reasonable technical and organizational measures to protect your data.
          </p>
          <h2 className="font-display text-2xl text-white pt-2">Your choices</h2>
          <p>
            You may request access, correction or deletion of your personal information, or opt out of communications, by
            contacting us. For SMS, reply STOP to unsubscribe.
          </p>
          <h2 className="font-display text-2xl text-white pt-2">Contact us</h2>
          <p>
            {BRAND.name} · Phone/Text: <a href={BRAND.phoneHref} className="text-cyan">{BRAND.phone}</a><br />
            Email: <a href="mailto:gutierreztony122012@icloud.com" className="text-cyan">gutierreztony122012@icloud.com</a>
          </p>
        </div>
      </Reveal>

      <div className="flex gap-4 mt-10">
        <Link to="/" className="btn-pill inline-flex items-center gap-2"><ArrowLeft size={15} /> Back home</Link>
        <Link to="/terms" className="btn-pill inline-flex items-center gap-2">Terms & Conditions</Link>
      </div>
    </div>
  );
}
